import TileWrapper from '../TileWrapper'
import { Link } from 'react-router-dom'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Users } from 'lucide-react'
import dayjs from 'dayjs'

import { useOverviewData } from 'app/frontend/base/config/queries'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterClassIcon } from 'app/frontend/base/components'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { useTile } from '../../context'
import { buttonVariants } from '@/components/ui/button'
import TableView from './PendingApplicants/TableView'
import { formatAge } from 'app/frontend/base/config/column-definitions/application'
import Card from 'app/frontend/base/components/Card'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { cn } from '@/lib/utils'

const PendingApplicantsTile = () => {
  const { team } = useTeam()
  const { tile } = useTile()
  const { data: overviewData } = useOverviewData(team.id)
  const applications = (overviewData?.pendingApplications || []).sort((a, b) =>
    dayjs(a.createdAt).diff(dayjs(b.createdAt)),
  )

  if (applications.length === 0) {
    if (tile.config?.empty_behavior === 'hide') {
      return null
    }

    return (
      <TileWrapper>
        <p className='text-muted-foreground'>No pending applications.</p>
      </TileWrapper>
    )
  }

  return (
    <TileWrapper
      inContainer={tile.position === 'sidebar'}
      cta={
        <Link to={`${team.path}/applicants`}>
          <Badge variant='outline' className='text-base gap-2'>
            <Users className='h-4 w-4 text-muted-foreground' />
            <span>{applications.length}</span>
          </Badge>
        </Link>
      }
    >
      {tile.position === 'main' ? (
        <TableView />
      ) : (
        <div className='space-y-2'>
          <ul className='space-y-2'>
            {applications.slice(0, tile.config?.amount ?? 5).map((app) => (
              <li key={app.id}>
                <Card>
                  <Link
                    to={`${team.path}/applicants/${app.id}`}
                    className='flex items-center justify-between gap-2'
                  >
                    <CharacterRoleIcon role={app.role} size='small' />
                    {app.characterReference ? (
                      <CharacterClassIcon
                        characterClass={app.characterReference?.characterClass}
                        size='small'
                        style='round'
                      />
                    ) : null}
                    <div
                      className={cn(
                        'grow',
                        app.characterReference?.characterClass &&
                          classColours[app.characterReference.characterClass]
                            .text,
                      )}
                    >
                      {app.characterReference?.name ??
                        app.userReference?.obfuscatedBattletag ??
                        'Unknown'}
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        {formatAge(app.createdAt)}
                      </TooltipTrigger>
                      <TooltipContent>
                        Submitted on{' '}
                        {dayjs(app.createdAt).format('D MMM YYYY, HH:mm')}
                      </TooltipContent>
                    </Tooltip>
                  </Link>
                </Card>
              </li>
            ))}
          </ul>

          {applications.length > (tile.config?.amount ?? 5) && (
            <div className='flex justify-end'>
              <Link
                to={`${team.path}/applicants`}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                View all
              </Link>
            </div>
          )}
        </div>
      )}
    </TileWrapper>
  )
}

export default PendingApplicantsTile
